import { cn } from '@/shared/lib/utils';
import type { CascaderOption } from '../helpers';
import { isPathSelected } from '../utils/path-utils';
import { CascaderOptionItem } from './CascaderOptionItem';

/**
 * カスケーダーカラムコンポーネントのプロパティ
 */
type CascaderColumnProps = {
  /** 現在の階層レベル（0から開始） */
  level: number;
  /** 現在のレベルで表示するオプション */
  options: CascaderOption[];
  /** 現在アクティブなパス */
  activePath: string[];
  /** 選択済みのパスリスト（複数選択モード用） */
  selectedPaths: string[][];
  /** ホバー中のインデックス */
  hoveredIndex: number[];
  /** 複数選択モードかどうか */
  multiple: boolean;
  /** 末端ノードのみ選択可能かどうか */
  onlyLeafSelectable: boolean;
  /** 最後のカラムかどうか */
  isLastColumn: boolean;
  /** チェックボックストグル時のコールバック */
  onCheckboxToggle: (option: CascaderOption, level: number) => void;
  /** ナビゲーション時のコールバック */
  onNavigate: (option: CascaderOption, level: number) => void;
  /** マウスエンター時のコールバック */
  onMouseEnter: (level: number, idx: number) => void;
  /** マウスリーブ時のコールバック */
  onMouseLeave: (level: number) => void;
};

/**
 * カスケーダーの1つのカラムを表示するコンポーネント
 *
 * 階層構造を持つカスケーダーの1つの列を表示し、オプション選択やナビゲーションを管理します。
 *
 * @param props - CascaderColumnProps
 * @returns JSX.Element | null
 */
export const CascaderColumn = ({
  level,
  options,
  activePath,
  selectedPaths,
  hoveredIndex,
  multiple,
  onlyLeafSelectable,
  isLastColumn,
  onCheckboxToggle,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: CascaderColumnProps) => {
  if (options.length === 0) return null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Mouse leave event is for hover state management only
    <div
      key={level}
      onMouseLeave={() => onMouseLeave(level)}
      className={cn(
        'min-w-[140px] max-w-[200px] overflow-y-auto border-border-base border-r last:border-r-0',
        !isLastColumn && 'pr-1',
        isLastColumn && 'border-r-0',
      )}
    >
      {options.map((option, idx) => {
        // currentPath を計算
        // Level 0 の場合は option.value のみ、それ以外は activePath に基づく
        const currentPath =
          level === 0
            ? [option.value]
            : activePath.length >= level
              ? [...activePath.slice(0, level), option.value]
              : [option.value]; // フォールバック

        const isActive = activePath[level] === option.value;
        const isSelected = multiple && isPathSelected(currentPath, selectedPaths);
        const isHovered = hoveredIndex[level] === idx;

        return (
          <CascaderOptionItem
            key={option.value}
            option={option}
            level={level}
            isActive={isActive}
            isSelected={isSelected}
            isHovered={isHovered}
            multiple={multiple}
            onlyLeafSelectable={onlyLeafSelectable}
            onCheckboxToggle={() => onCheckboxToggle(option, level)}
            onNavigate={() => onNavigate(option, level)}
            onMouseEnter={() => onMouseEnter(level, idx)}
          />
        );
      })}
    </div>
  );
};
